import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'

interface Props {
    onSuccess: string | ((result: unknown) => void)
    entryId?: number
}

function EditCurrencyWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        if (props.entryId !== undefined) {
            return API.get(`/currencies/${props.entryId}`).then((r: any) => ({ entry: r.data }))
        }
        return Promise.resolve({ entry: null })
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        return {
            component: form(lr.entry, completion),
            title: props.entryId ? 'Edit currency' : 'New currency'
        }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        if (props.entryId) {
            return API.put(`/currencies/${props.entryId}`, user_input)
        } else {
            return API.post('/currencies', user_input)
        }
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result && (execution_result as any).data)
        }
    }

    function form(previousValues: any, completion: (input: unknown) => void) {

        const schema = yup.object().shape({
            name: yup.string().required(),
            euroValue: yup.number().required().min(0)
        })

        const initialValues = previousValues ? {
            name: previousValues.name,
            euroValue: previousValues.euroValue
        } : {
            name: '',
            euroValue: ''
        }

        const parseForm = (values: any) => {
            completion({
                name: values.name,
                euroValue: parseFloat(values.euroValue)
            })
        }

        return (
            <Formik validationSchema={schema} onSubmit={parseForm} initialValues={initialValues}>
                {({ handleSubmit, handleChange, values, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Row>
                            <Form.Group as={Col} controlId="formName">
                                <Form.Label>Currency code</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    placeholder="e.g. EUR, USD"
                                    value={values.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} controlId="formEuroValue">
                                <Form.Label>Euro exchange rate</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="euroValue"
                                    placeholder="e.g. 1.0 for EUR, 0.85 for USD"
                                    value={values.euroValue}
                                    onChange={handleChange}
                                    isInvalid={!!errors.euroValue}
                                />
                                <Form.Control.Feedback type="invalid">{errors.euroValue}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Button variant="primary" type="submit">Save</Button>
                    </Form>
                )}
            </Formik>
        )
    }

    return (
        <OperationWorkflowModal
            loadingStep={loadingStep}
            userInputStep={userInputStep}
            executionStep={executionStep}
            completionCallback={completionCallback}
        />
    )
}

export default EditCurrencyWorkflow
